import { useRef, useState } from 'react';
import { Box, Checkbox, Chip, ClickAwayListener, FormControlLabel, IconButton, InputAdornment, Paper, Popper, TextField, Typography } from '@mui/material';
import { SimpleTreeView } from '@mui/x-tree-view/SimpleTreeView';
import { TreeItem } from '@mui/x-tree-view/TreeItem';
import { ClearIcon } from '../Icons';

function NiveauClasseSelect({ classroomOptions, classroomsState, setClassroomsState }) {

    const [open, setOpen] = useState(false);
    const [searchText, setSearchText] = useState('');
    const anchorRef = useRef(null);

    const isChecked = (uid) => Boolean(classroomsState.find((c) => c.uid === uid)?.checked);

    const toggleClassroom = (c) => {
        setClassroomsState(classroomsState.map((i) => (i.uid === c.uid ? { ...i, checked: !i.checked } : i)));
    };

    const toggleGrade = (grade) => {
        const childIds = grade.choices.map((c) => c.uid);
        const allChecked = childIds.every(isChecked);
        setClassroomsState(classroomsState.map((i) => (childIds.includes(i.uid) ? { ...i, checked: !allChecked } : i)));
    };

    const fullyCheckedGrades = classroomOptions.filter((g) => g.choices.length > 0 && g.choices.every((c) => isChecked(c.uid)));
    const fullyCheckedGradeUids = new Set(fullyCheckedGrades.map((g) => g.uid));
    const individualTags = classroomsState.filter((c) => c.checked && !fullyCheckedGradeUids.has(c.gradeUid));
    const hasSelection = fullyCheckedGrades.length + individualTags.length > 0;

    const clearAll = () => {
        setClassroomsState(classroomsState.map((c) => ({ ...c, checked: false })));
        setSearchText('');
    };

    const search = searchText.trim().toLowerCase();
    const filteredOptions = search
        ? classroomOptions
            .map((g) => ({
                ...g,
                choices: g.name.toLowerCase().includes(search)
                    ? g.choices
                    : g.choices.filter((c) => c.name.toLowerCase().includes(search)),
            }))
            .filter((g) => g.choices.length > 0)
        : classroomOptions;

    if (classroomOptions.length === 0) return null;

    return (
        <ClickAwayListener onClickAway={() => setOpen(false)}>
            <Box>
                <TextField
                    ref={anchorRef}
                    size="small"
                    fullWidth
                    variant="outlined"
                    placeholder={hasSelection ? '' : 'Rechercher un niveau ou une classe...'}
                    value={searchText}
                    onChange={(e) => { setSearchText(e.target.value); setOpen(true); }}
                    onFocus={() => setOpen(true)}
                    InputProps={{
                        endAdornment: hasSelection && (
                            <InputAdornment position="end">
                                <IconButton size="small" onClick={clearAll} onMouseDown={(e) => e.stopPropagation()} title="Tout effacer">
                                    <ClearIcon fontSize="small" />
                                </IconButton>
                            </InputAdornment>
                        ),
                        startAdornment: hasSelection && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, py: 0.5 }}>
                                {fullyCheckedGrades.map((g) => (
                                    <Chip
                                        key={g.uid}
                                        size="small"
                                        label={g.name}
                                        onDelete={() => toggleGrade(g)}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        sx={{ bgcolor: 'grey.700', color: 'common.white', '& .MuiChip-deleteIcon': { color: 'grey.300' } }}
                                    />
                                ))}
                                {individualTags.map((c) => (
                                    <Chip key={c.uid} size="small" label={c.name} onDelete={() => toggleClassroom(c)} onMouseDown={(e) => e.stopPropagation()} />
                                ))}
                            </Box>
                        ),
                    }}
                />
                <Popper
                    open={open}
                    anchorEl={anchorRef.current}
                    placement="bottom-start"
                    style={{ zIndex: 1300, width: anchorRef.current ? anchorRef.current.offsetWidth : undefined }}
                >
                    <Paper elevation={4} sx={{ mt: 0.5, maxHeight: 320, overflowY: 'auto', p: 1 }}>
                        {filteredOptions.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>Aucun résultat</Typography>
                        ) : (
                            <SimpleTreeView defaultExpandedItems={filteredOptions.map((g) => g.uid)}>
                                {filteredOptions.map((grade) => {
                                    const childIds = grade.choices.map((c) => c.uid);
                                    const allChecked = childIds.length > 0 && childIds.every(isChecked);
                                    const someChecked = childIds.some(isChecked);
                                    return (
                                        <TreeItem
                                            key={grade.uid}
                                            itemId={grade.uid}
                                            label={
                                                <FormControlLabel
                                                    onClick={(e) => e.stopPropagation()}
                                                    control={
                                                        <Checkbox
                                                            size="small"
                                                            checked={allChecked}
                                                            indeterminate={someChecked && !allChecked}
                                                            onChange={() => toggleGrade(grade)}
                                                        />
                                                    }
                                                    label={<Typography sx={{ fontWeight: 'bold' }}>{grade.name}</Typography>}
                                                />
                                            }
                                        >
                                            {grade.choices.map((classroom) => (
                                                <TreeItem
                                                    key={classroom.uid}
                                                    itemId={classroom.uid}
                                                    label={
                                                        <FormControlLabel
                                                            onClick={(e) => e.stopPropagation()}
                                                            control={
                                                                <Checkbox
                                                                    size="small"
                                                                    checked={isChecked(classroom.uid)}
                                                                    onChange={() => toggleClassroom(classroom)}
                                                                />
                                                            }
                                                            label={classroom.name}
                                                        />
                                                    }
                                                />
                                            ))}
                                        </TreeItem>
                                    );
                                })}
                            </SimpleTreeView>
                        )}
                    </Paper>
                </Popper>
            </Box>
        </ClickAwayListener>
    );
}

export default NiveauClasseSelect;
